import { RoleName } from '.'
import { ROLE_MODULES, SwitchableModule } from '../modules'
import { UserPermission } from '../permissions'
import { extractRoleNames, hasMatchingRoles } from './utils'

// -- WHAT IS THIS? ------------------------------------------------------------
// The core permissions system is binary by nature (a user has a permission or not).
// These rules extensions are a behavioral layer that allows
// for more nuanced access control based on roles.
// -----------------------------------------------------------------------------

export const ROLES_RULES = {
  ATTACH_ASSIGNED_GEOGRAPHY: new Set<RoleName>(['TMAR', 'KEY_ACCOUNT']), // To attach assigned geography to the user (useMe hook)
  RESTRICT_BY_ASSIGNED_TERRITORIES: new Set<RoleName>(['TMAR', 'KEY_ACCOUNT']), // To force filtering by available territories
  FILTER_BY_OUTLET_TYPE: new Set<RoleName>(['KEY_ACCOUNT']), // To force filtering by relevant outlet types in Flagged Orders
} as const

export const ROLES_RULES_DESCRIPTIONS = {
  ATTACH_ASSIGNED_GEOGRAPHY: 'Attaching assigned geography to the user',
  RESTRICT_BY_ASSIGNED_TERRITORIES: 'Restricting data access by assigned territories',
  FILTER_BY_OUTLET_TYPE: 'Filtering by relevant outlet types in Flagged Orders',
} as const satisfies Record<keyof typeof ROLES_RULES, string>

// ------------------------------------------------------------------
// -- ROLE MANAGEMENT RULES -----------------------------------------
// ------------------------------------------------------------------

/**
 * The two admin roles, both of which belong to core. `users_manageAdmins` is a core permission and
 * gates exactly these: it has no bearing on CRT_ADMIN or CREDIT_ADMIN, which are ordinary roles inside
 * their own modules, granted by whoever holds that module's manage-roles permission.
 */
export const CORE_ADMIN_ROLES: RoleName[] = ['BUSINESS_ADMIN', 'IT_ADMIN']

/**
 * Which permission grants role management in each module. Each permission is only meaningful inside
 * its own module: `users_manageAccess` is the Fuze app's, and CRT and Credit have their own.
 */
const MANAGE_ROLES_PERMISSION: Record<SwitchableModule, UserPermission> = {
  core: 'users_manageAccess',
  crt: 'crt_users_manageAccess',
  credit: 'credit_users_manageAccess',
}

/**
 * Whether the acting user may assign or remove `role` while working in `module`.
 *
 * Two separate questions, in order: does the role belong to the module being worked in, and is the
 * actor entitled to grant it there.
 *
 * The module test comes first and applies to everyone, super admins included. Previously
 * `users_manageAdmins` short-circuited the whole function, so an IT_ADMIN could assign CRT or Credit
 * roles from any screen, and `core` had no module branch at all, so a core actor with
 * `users_manageAccess` could assign `CRT_ADMIN`. Both are now refused, and `users_manageAdmins` is
 * reduced to what its name says: the gate for the two core admin roles.
 *
 * The `SUPPORT_ALLOWED_TARGET_ROLES` allowlist is gone, which narrows two things deliberately: SUPPORT
 * can no longer assign `BUSINESS_ADMIN`, since that role now sits behind `users_manageAdmins`, and an
 * actor holding none of the manage-roles permissions can no longer target the nine roles the allowlist
 * used to permit as a fallback. Both were ways to grant a role without the permission that governs it.
 *
 * Takes the module the actor is *currently working in* (the module switcher's active module), not a
 * module derived from the roles they happen to hold. There is no cross-module role management: a user
 * with roles in several modules administers each one from inside that module.
 */
export const canTargetRole = (
  role: RoleName,
  userPermissions: UserPermission[],
  module: SwitchableModule,
) => {
  const roleModule = ROLE_MODULES[role]

  if (roleModule !== module) return false

  if (CORE_ADMIN_ROLES.includes(role) && !userPermissions.includes('users_manageAdmins'))
    return false
  return userPermissions.includes(MANAGE_ROLES_PERMISSION[roleModule])
}

// ------------------------------------------------------------------
// -- GEOGRAPHY RULES -----------------------------------------------
// ------------------------------------------------------------------

export const shouldAttachAssignedGeography = (userRoles: RoleName[]) =>
  hasMatchingRoles(userRoles, ROLES_RULES.ATTACH_ASSIGNED_GEOGRAPHY)

const UNRESTRICTED_GEOGRAPHY_PERMISSIONS: UserPermission[] = ['map_viewAllAreas']

export const shouldRestrictByAssignedGeography = (
  userRoles: RoleName[] | { name: RoleName; id: number }[],
  userPermissions?: UserPermission[],
) => {
  const canAccessAllGeography =
    userPermissions &&
    userPermissions?.some((permission) => UNRESTRICTED_GEOGRAPHY_PERMISSIONS.includes(permission))

  if (canAccessAllGeography) return false

  const roleNames = extractRoleNames(userRoles)
  return hasMatchingRoles(roleNames, ROLES_RULES.RESTRICT_BY_ASSIGNED_TERRITORIES)
}

// ------------------------------------------------------------------
// -- OUTLET TYPE RULES -----------------------------------------------
// ------------------------------------------------------------------

export const shouldFilterByOutletType = (
  userRoles: RoleName[] | { name: RoleName; id: number }[],
) => {
  const roleNames = extractRoleNames(userRoles)
  return hasMatchingRoles(roleNames, ROLES_RULES.FILTER_BY_OUTLET_TYPE)
}
